<template>
  <!-- 地图容器：必须设置宽高，否则无法渲染 -->
  <div
    ref="mapContainer"
    class="baidu-map-container"
    :style="{ width: mapWidth, height: mapHeight }"
  ></div>
</template>

<script>
export default {
  name: "BaiduMapViewer",
  props: {
    // 核心：目标经纬度 [经度, 纬度]（必传）
    lngLat: {
      type: Array,
      required: true,
      validator: (val) => val.length === 2 && !val.some((item) => isNaN(item)),
    },
    // 百度地图浏览器端AK（必传，需提前配置白名单）
    ak: {
      type: String,
      required: true,
      validator: (val) => val.trim() !== "",
    },
    // 地图缩放级别（1-19，默认14，数值越大越精细）
    zoom: {
      type: Number,
      default: 14,
      validator: (val) => val >= 1 && val <= 19,
    },
    // 是否显示定位标记
    showMarker: {
      type: Boolean,
      default: true,
    },
    // 标记悬浮提示文字
    markerTitle: {
      type: String,
      default: "目标位置",
    },
    // 地图宽高（支持百分比/像素，默认100%宽、500px高）
    mapWidth: {
      type: String,
      default: "100%",
    },
    mapHeight: {
      type: String,
      default: "500px",
    },
    // 可选：限制地图显示的城市（避免跨城）
    city: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      map: null, // 百度地图实例
      marker: null, // 定位标记实例
      mapLoaded: false, // SDK是否加载完成
    };
  },
  watch: {
    // 监听经纬度变化，重新定位地图
    lngLat: {
      deep: true,
      handler(newVal) {
        if (this.mapLoaded && newVal.length === 2) {
          // 确保经纬度是数字类型
          const lng = parseFloat(newVal[0]);
          const lat = parseFloat(newVal[1]);

          // 检查经纬度是否有效
          if (!isNaN(lng) && !isNaN(lat)) {
            // 尝试使用普通版 BMap 对象
            let mapObj = window.BMap;

            // 如果没有普通版，尝试使用 GL 版
            if (!mapObj || !mapObj.Point) {
              mapObj = window.BMap;
            }

            if (mapObj && mapObj.Point) {
              try {
                const targetPoint = new mapObj.Point(lng, lat);
                this.map.centerAndZoom(targetPoint, this.zoom);
                // 更新标记位置
                if (this.showMarker && this.marker) {
                  this.marker.setPosition(targetPoint);
                }
              } catch (error) {
                console.error("更新地图位置失败:", error);
              }
            }
          }
        }
      },
    },
    // 监听缩放级别变化
    zoom(newVal) {
      if (this.mapLoaded) {
        this.map.setZoom(newVal);
      }
    },
  },
  mounted() {
    // 确保DOM渲染完成后加载SDK
    this.$nextTick(() => {
      this.loadBaiduMapSDK();
    });
  },
  beforeDestroy() {
    // 销毁地图实例，避免内存泄漏
    if (this.map) {
      window.BMap && window.BMap.event.clearInstanceListeners(this.map);
      this.map = null;
      this.marker = null;
      this.mapLoaded = false;
    }
  },
  methods: {
    /**
     * 加载百度地图GL版SDK（避免重复加载）
     */
    loadBaiduMapSDK() {
      // 已加载SDK（BMap 或 BMap），直接初始化
      if (
        (window.BMap && window.BMap.Point && window.BMap.Map) ||
        (window.BMap && window.BMap.Point && window.BMap.Map)
      ) {
        this.initMap();
        return;
      }

      // 动态创建script标签加载SDK
      const script = document.createElement("script");
      // 使用普通版SDK替代WebGL版，提高兼容性
      script.src = `https://api.map.baidu.com/api?v=3.0&ak=${this.ak}&callback=baiduMapInit`;
      script.type = "text/javascript";

      // 全局回调函数
      window.baiduMapInit = () => {
        console.log("百度地图 SDK 加载成功");
        // 延迟一下，确保SDK完全加载
        setTimeout(() => {
          this.initMap();
        }, 100);
      };

      script.onerror = () => {
        console.error("百度地图 SDK 加载失败");
        this.$emit(
          "error",
          "百度地图SDK加载失败，请检查网络连接或AK是否有效！",
        );
      };

      // 检查是否已经存在百度地图脚本
      const existingScript = document.querySelector(
        'script[src*="api.map.baidu.com"]',
      );
      if (!existingScript) {
        document.body.appendChild(script);
      } else {
        // 如果已经存在，等待一下再初始化
        setTimeout(() => {
          this.initMap();
        }, 500);
      }
    },

    /**
     * 初始化地图核心逻辑
     */
    initMap() {
      // 尝试使用普通版 BMap 对象
      let mapObj = window.BMap;
      let mapType = "BMap";

      // 如果没有普通版，尝试使用 GL 版
      if (!mapObj || !mapObj.Point || !mapObj.Map) {
        mapObj = window.BMap;
        mapType = "BMap";
      }

      // 检查地图对象是否存在
      if (!mapObj || !mapObj.Point || !mapObj.Map) {
        console.error("百度地图 SDK 加载失败，地图对象不存在或不完整");
        this.$emit("error", "地图加载失败，请检查网络连接或 AK 是否正确！");
        return;
      }

      console.log(`使用 ${mapType} 对象初始化地图`);

      // 1. 创建经纬度点对象（百度：Point(经度, 纬度)）
      // 确保经纬度是数字类型
      const lng = parseFloat(this.lngLat[0]);
      const lat = parseFloat(this.lngLat[1]);

      // 检查经纬度是否有效
      if (isNaN(lng) || isNaN(lat)) {
        console.error("无效的经纬度数据:", this.lngLat);
        this.$emit("error", "无效的经纬度数据");
        return;
      }

      try {
        const targetPoint = new mapObj.Point(lng, lat);

        // 2. 初始化地图实例
        this.map = new mapObj.Map(this.$refs.mapContainer);
        // 设置地图中心点和缩放级别
        this.map.centerAndZoom(targetPoint, this.zoom);
        // 开启滚轮缩放
        this.map.enableScrollWheelZoom(true);
        // 开启连续缩放
        if (mapObj.Map.prototype.enableContinuousZoom) {
          this.map.enableContinuousZoom(true);
        }
        // 可选：限制地图显示城市
        if (this.city && this.map.setCurrentCity) {
          this.map.setCurrentCity(this.city);
        }

        // 3. 添加定位标记（可选）
        if (this.showMarker) {
          this.createMarker(targetPoint, mapObj);
        }

        this.mapLoaded = true;
        // 通知父组件地图初始化完成
        this.$emit("init-success", {
          lng: lng,
          lat: lat,
          zoom: this.zoom,
        });
      } catch (error) {
        console.error("初始化地图失败:", error);
        this.$emit("error", "地图初始化失败，请重试！");
      }
    },

    /**
     * 创建地图标记
     * @param {BMap.Point} point 标记位置
     * @param {Object} BMap 百度地图GL对象
     */
    createMarker(point, mapObj) {
      try {
        // 创建标记实例
        this.marker = new mapObj.Marker(point, {
          title: this.markerTitle, // 鼠标悬浮提示
          enableDragging: false, // 是否允许拖动标记（默认禁用）
        });
        // 将标记添加到地图
        this.map.addOverlay(this.marker);

        // 可选：点击标记弹出信息窗口
        if (mapObj.InfoWindow) {
          const infoWindow = new mapObj.InfoWindow(this.markerTitle, {
            width: 200,
            height: 50,
          });
          this.marker.addEventListener("click", () => {
            this.map.openInfoWindow(infoWindow, point);
          });
        }
      } catch (error) {
        console.error("创建标记失败:", error);
      }
    },
  },
};
</script>

<style scoped>
/* 地图容器基础样式，避免默认边距 */
.baidu-map-container {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}
</style>



// WEBPACK FOOTER //
// src/components/BaiduMapViewer/index.vue